import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Footer from './Footer'
import { ChevronLeftIcon, PlusIcon, TrashIcon, PrimaryButton } from './ui'
import { useApi } from '../services/api'
import { usePortalAuth } from '../auth/authContexts'
import { isDevMode } from '../utils/devMode'
import { LEGACY_MULTI_VEHICLE_CUSTOMER_MESSAGE } from '../utils/policyReferralState'
import { useApiError } from '../hooks/useApiError'
import { usePolicySystemStatus } from '../context/PolicySystemStatusProvider'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'
import {
  shouldSignOutForPolicyLinkOtpError,
  storePolicyLinkOtpLockoutMessage,
  type PolicyLinkOtpApiErrorBody,
} from '../utils/policyLinkOtpLockout'
import {
  CLUB4X4_PHONE_DISPLAY,
  CLUB4X4_PHONE_TEL,
} from '../claims/contactLinks'
import { portalMarketingLoginReturnUrl, redirectToClub4x4Website } from '../utils/portalMarketingRedirect'

type ConfirmDetailsLocationState = {
  linkAdditionalPolicy?: boolean
  policyNumbers?: string[]
  policyNumber?: string
  firstName?: string
  lastName?: string
  email?: string
}

export default function ConfirmDetails() {
  const navigate = useNavigate()
  const location = useLocation()
  const { linkPolicy } = useApi()
  const { logout } = usePortalAuth()
  const routeState = (location.state ?? null) as ConfirmDetailsLocationState | null
  const isLinkAdditionalPolicy = routeState?.linkAdditionalPolicy === true
  const [formData, setFormData] = useState(() => ({
    firstName: routeState?.firstName?.trim() ?? '',
    lastName: routeState?.lastName?.trim() ?? '',
    email: routeState?.email?.trim() ?? '',
  }))
  const [policyNumbers, setPolicyNumbers] = useState<string[]>(() => {
    const fromRoute =
      routeState?.policyNumbers?.map((n) => String(n).trim()).filter((n) => n !== '') ?? []
    if (fromRoute.length > 0) return fromRoute
    const single = routeState?.policyNumber?.trim()
    if (single) return [single]
    return ['']
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [linkError, setLinkError] = useState<unknown>(null)
  const policySystem = usePolicySystemStatus()
  const resolvedLinkError = useApiError(linkError, policySystem, 'linkPolicy')

  const handleSwitchToExistingAccountLogin = async () => {
    if (isDevMode()) {
      navigate('/signin', { replace: true })
      return
    }
    sessionStorage.removeItem('club4x4_visited_confirm_details')
    try {
      await logout({ returnTo: portalMarketingLoginReturnUrl() })
    } catch (logoutErr) {
      console.error('[ConfirmDetails] switch to login failed:', logoutErr)
    }
  }
  useScrollToTopOnErrorSignal(errorMessage ?? resolvedLinkError?.body ?? null)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePolicyNumberChange = (index: number, value: string) => {
    const updated = [...policyNumbers]
    updated[index] = value
    setPolicyNumbers(updated)
  }

  const handleAddPolicy = () => {
    setPolicyNumbers([...policyNumbers, ''])
  }

  const handleRemovePolicy = (index: number) => {
    if (policyNumbers.length > 1) {
      const updated = policyNumbers.filter((_, i) => i !== index)
      setPolicyNumbers(updated)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const policyNumbersEntered = policyNumbers.filter((n) => n.trim() !== '')
    if (policyNumbersEntered.length === 0) {
      setErrorMessage('Please enter at least one policy number.')
      return
    }
    setErrorMessage(null)
    setIsSubmitting(true)
    try {
      const result = await linkPolicy({
        firstName: isLinkAdditionalPolicy ? '' : formData.firstName.trim(),
        lastName: isLinkAdditionalPolicy ? '' : formData.lastName.trim(),
        email: isLinkAdditionalPolicy ? '' : formData.email.trim(),
        policyNumbers: policyNumbersEntered,
      })
      if (result?.otpRequired && result.challengeId) {
        navigate('/confirm-details/verify', {
          replace: true,
          state: {
            challengeId: result.challengeId,
            maskedEmail: result.maskedEmail ?? '',
            expiresAtUtc: result.expiresAtUtc,
            linkAdditionalPolicy: isLinkAdditionalPolicy,
          },
        })
        return
      }
      if (result?.otpRequired && !result.challengeId) {
        setErrorMessage('We could not start email verification. Please try again or contact support.')
        return
      }
      if (result?.linked) {
        console.error(
          '[ConfirmDetails] linkPolicy returned linked without OTP — API may not be deployed with policy-link verification yet.',
          result,
        )
        setErrorMessage(
          'Email verification is required before we can link your policy. Please try again in a few minutes or contact support on 1300 296 296.',
        )
        return
      }
      setErrorMessage('We could not start linking your policy. Please try again.')
    } catch (err: unknown) {
      const e = err as Error & {
        apiBody?: PolicyLinkOtpApiErrorBody & {
          failedPolicies?: Array<{ policyRef?: string; code?: string }>;
        };
      };
      if (shouldSignOutForPolicyLinkOtpError(e?.apiBody)) {
        storePolicyLinkOtpLockoutMessage(e?.apiBody);
        try {
          await logout();
        } catch (logoutErr) {
          console.error('[ConfirmDetails] logout failed:', logoutErr);
        }
        navigate('/signin', { replace: true });
        return;
      }
      if (isLinkAdditionalPolicy && e?.apiBody?.code === 'NO_POLICIES_LINKED') {
        setErrorMessage('We could not link that policy to your account. Please call our support team on 1300 296 296.')
        return
      }
      if (e?.apiBody?.failedPolicies?.some((fp) => fp.code === 'LEGACY_MULTI_VEHICLE')) {
        setErrorMessage(LEGACY_MULTI_VEHICLE_CUSTOMER_MESSAGE)
        return
      }
      if (
        e?.apiBody?.code === 'DETAILS_MISMATCH' ||
        e?.apiBody?.code === 'POLICY_NOT_FOUND' ||
        e?.apiBody?.code === 'NO_POLICIES_LINKED'
      ) {
        const fromFailed = (e?.apiBody?.failedPolicies ?? [])
          .map((fp) => String(fp?.policyRef ?? '').trim())
          .filter(Boolean)
        const policyNumbersForRetry = Array.from(new Set(fromFailed))
        const retryPolicyNumbers =
          policyNumbersForRetry.length > 0 ? policyNumbersForRetry : [...policyNumbersEntered]
        navigate('/check-your-details', {
          replace: true,
          state: {
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            email: formData.email.trim(),
            policyNumbers: retryPolicyNumbers,
            policyNumber: retryPolicyNumbers[0] ?? '',
            linkAdditionalPolicy: isLinkAdditionalPolicy,
          },
        })
        return
      }
      setErrorMessage(e?.message ?? 'Something went wrong. Please try again.')
      setLinkError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-background-brand-primary flex flex-col items-center justify-center min-h-screen w-full">
      <main id="main" className="flex w-full flex-col items-center justify-center">
      <div className="flex flex-col gap-[32px] items-center justify-center px-[16px] py-[80px] relative w-full">
        {/* Back button */}
        {isLinkAdditionalPolicy ? (
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="cursor-pointer flex gap-[4px] items-end p-0 relative w-full max-w-sm bg-transparent border-0"
          >
            <div className="overflow-clip relative shrink-0 size-5 text-foreground-brand-secondary-alt">
              <ChevronLeftIcon />
            </div>
            <p className="font-body font-semibold text-sm leading-sm text-text-brand-secondary-700">
              Back to dashboard
            </p>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              if (isDevMode()) {
                navigate('/')
                return
              }
              redirectToClub4x4Website()
            }}
            className="cursor-pointer flex gap-[4px] items-end p-0 relative w-full max-w-sm bg-transparent border-0"
          >
            <div className="overflow-clip relative shrink-0 size-5 text-foreground-brand-secondary-alt">
              <ChevronLeftIcon />
            </div>
            <p className="font-body font-semibold text-sm leading-sm text-text-brand-secondary-700">
              Back to Club 4X4
            </p>
          </button>
        )}

        {/* Confirm details card container */}
        <div className="flex items-center justify-center px-[16px] py-0 relative shrink-0 w-full">
          <div className="bg-background-primary flex flex-[1_0_0] flex-col items-start max-w-sm overflow-clip relative rounded-xl shadow-lg shrink-0 w-full">
            {/* Header with logo */}
            <div className="bg-foreground-brand-primary-600 flex flex-col items-center justify-center px-[24px] py-[20px] relative w-full">
              <div className="flex items-start relative w-[200px]">
                <div className="aspect-[596/159] flex-1 min-h-px min-w-px overflow-clip relative">
                  <img 
                    src="/Clip path group.svg" 
                    alt="Club 4X4" 
                    className="h-full w-auto object-contain brightness-0 invert"
                  />
                </div>
              </div>
            </div>

            {/* Form content */}
            <div className="flex flex-col gap-[32px] items-start px-[24px] py-[40px] relative w-full">
              {/* Header */}
              <div className="flex flex-col gap-[24px] items-center relative w-full">
                <div className="flex flex-col gap-[8px] items-start text-center w-full">
                  <h1 className="m-0 font-display leading-[32px] relative shrink-0 text-text-primary-900 text-display-xs w-full">
                    Confirm your details
                  </h1>
                  <p className="font-body font-regular text-md leading-md text-text-tertiary-600 w-full">
                    Use the same details as your policy documents (e.g. Certificate of Insurance).
                  </p>
                </div>
              </div>

              {/* Form sections */}
              <div className="flex flex-col gap-[40px] items-center rounded-lg w-full">
                {/* Policy holder details section */}
                <div className="flex flex-col gap-[24px] items-start relative w-full">
                  {!isLinkAdditionalPolicy && (
                    <p className="font-body font-medium text-lg leading-lg text-text-primary-900 w-full">
                      Policy holder details
                    </p>
                  )}
                  {!isLinkAdditionalPolicy && (
                    <div className="flex flex-col gap-[20px] items-start relative w-full">
                    {/* First Name */}
                    <div className="flex flex-col gap-[6px] items-start w-full">
                      <label htmlFor="confirm-firstName" className="font-body font-medium text-sm leading-sm text-text-secondary-700">
                        First name
                      </label>
                      <input
                        id="confirm-firstName"
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="bg-background-primary border border-border-primary border-solid px-[14px] py-[10px] rounded-md shadow-xs w-full text-md text-text-primary-900 focus:outline-none focus:ring-2 focus:ring-brand-border"
                      />
                    </div>

                    {/* Last Name */}
                    <div className="flex flex-col gap-[6px] items-start w-full">
                      <label htmlFor="confirm-lastName" className="font-body font-medium text-sm leading-sm text-text-secondary-700">
                        Last name
                      </label>
                      <input
                        id="confirm-lastName"
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="bg-background-primary border border-border-primary border-solid px-[14px] py-[10px] rounded-md shadow-xs w-full text-md text-text-primary-900 focus:outline-none focus:ring-2 focus:ring-brand-border"
                      />
                    </div>

                    {/* Email */}
                    <div className="flex flex-col gap-[6px] items-start w-full">
                      <label htmlFor="confirm-email" className="font-body font-medium text-sm leading-sm text-text-secondary-700">
                        Email
                      </label>
                      <input
                        id="confirm-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="bg-background-primary border border-border-primary border-solid px-[14px] py-[10px] rounded-md shadow-xs w-full text-md text-text-primary-900 focus:outline-none focus:ring-2 focus:ring-brand-border"
                      />
                    </div>
                    </div>
                  )}
                </div>

                {/* Link your policies section */}
                <div className="flex flex-col gap-[24px] items-start relative w-full">
                  <p className="font-body font-medium text-lg leading-lg text-text-primary-900 w-full">
                    Link your policies
                  </p>
                  <div className="flex flex-col gap-[20px] items-start relative w-full">
                    {/* Policy number inputs */}
                    {policyNumbers.map((policyNumber, index) => (
                      <div key={index} className={`flex ${index > 0 ? 'flex-row gap-[8px] items-end' : 'flex-col'} w-full`}>
                        <div className={`flex flex-col gap-[6px] items-start ${index > 0 ? 'flex-1' : 'w-full'}`}>
                          <label htmlFor={`confirm-policy-number-${index}`} className="font-body font-medium text-sm leading-sm text-text-secondary-700">
                            Policy number
                          </label>
                          <input
                            id={`policy-number-${index}`}
                            type="text"
                            value={policyNumber}
                            onChange={(e) => handlePolicyNumberChange(index, e.target.value)}
                            placeholder="Enter policy number"
                            className="bg-background-primary border border-border-primary border-solid px-[12px] py-[8px] rounded-md shadow-xs w-full text-md text-text-primary-900 placeholder:text-text-placeholder focus:outline-none focus:ring-2 focus:ring-brand-border"
                          />
                        </div>
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => handleRemovePolicy(index)}
                            className="bg-[#d92d20] flex items-center justify-center overflow-clip p-[10px] relative rounded-md shadow-xs shrink-0"
                            aria-label="Remove policy"
                          >
                            <div className="overflow-clip relative shrink-0 size-5 text-[#fda29b]">
                              <TrashIcon />
                            </div>
                          </button>
                        )}
                      </div>
                    ))}

                    {/* Add another policy button */}
                    <button
                      type="button"
                      onClick={handleAddPolicy}
                      className="bg-background-primary border border-border-primary border-solid flex gap-[4px] items-center justify-center overflow-clip px-[12px] py-[8px] relative rounded-md shadow-xs w-full"
                    >
                      <div className="overflow-clip relative shrink-0 size-5 text-text-tertiary-600">
                        <PlusIcon />
                      </div>
                      <span className="font-body font-semibold text-sm leading-sm text-text-secondary-700">
                        Add another policy
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-[16px] items-start relative w-full">
                {(resolvedLinkError?.body ?? errorMessage) && (
                  <p className="font-body font-regular text-sm leading-sm text-red-600 w-full" role="alert">
                    {resolvedLinkError?.body ?? errorMessage}
                  </p>
                )}
                <PrimaryButton type="submit" onClick={handleSubmit} fullWidth disabled={isSubmitting}>
                  {isSubmitting ? 'Linking…' : isLinkAdditionalPolicy ? 'Link policy' : 'Create account'}
                </PrimaryButton>

                <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600 w-full">
                  Need help? Call us on{' '}
                  <a
                    href={CLUB4X4_PHONE_TEL}
                    className="font-semibold text-foreground-brand-primary-600 hover:underline"
                  >
                    {CLUB4X4_PHONE_DISPLAY}
                  </a>
                </p>
              </div>

              {/* Sign in link — onboarding only (dashboard "add policy" passes linkAdditionalPolicy) */}
              {!isLinkAdditionalPolicy ? (
                <div className="content-baseline flex flex-wrap gap-[4px] items-baseline justify-center relative shrink-0 w-full">
                  <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600 relative shrink-0">
                    Already have an account?
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleSwitchToExistingAccountLogin()}
                    className="cursor-pointer flex gap-[4px] items-center justify-center overflow-clip p-0 relative shrink-0 bg-transparent border-0"
                  >
                    <span className="font-body font-semibold text-sm leading-sm text-text-brand-secondary-700">
                      Log in
                    </span>
                  </button>
                </div>
              ) : null}


            </div>
          </div>
        </div>
      </div>
      </main>

      <Footer />
    </div>
  )
}
